import { inject, Injectable } from '@angular/core';
import { NmsResource } from '@app/shared/resources/nms.resource';
import {
  BLEKeyDeliveryProgressApiResponse,
  BLEKeyDeliveryRetryApiResponse,
  GatewayJoinStatusResponse,
  LatestOpenJoiningSessionStatus,
  NodeJoiningStatusRecord,
  OpenJoiningStatus,
} from '@app/shared/models/nms.models';
import { map, Observable } from 'rxjs';

function formatRelativeTime(epochSeconds: number): string {
  const diffMs = Date.now() - epochSeconds * 1000;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec} sec ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  return `${diffHr} hr ago`;
}

@Injectable({
  providedIn: 'root'
})
export class NmsService {
  private readonly nmsResource = inject(NmsResource);

  isDeviceReadyForJoining(buildingId: number): Observable<OpenJoiningStatus> {
    return this.nmsResource.isDeviceReadyForJoining(buildingId);
  }

  startDeviceJoining(buildingId: number): Observable<string> {
    return this.nmsResource.startDeviceJoining(buildingId);
  }

  stopDeviceJoining(buildingId: number): Observable<string> {
    return this.nmsResource.stopDeviceJoining(buildingId);
  }

  getLatestOpenJoiningSessionStatus(buildingId: number): Observable<LatestOpenJoiningSessionStatus> {
    return this.nmsResource.getLatestOpenJoiningSessionStatus(buildingId);
  }

  getCurrentGatewayStatuses(buildingId: number): Observable<GatewayJoinStatusResponse[]> {
    return this.nmsResource.getCurrentGatewayStatuses(buildingId);
  }

  mapNodeJoiningStatusRows(session: LatestOpenJoiningSessionStatus): NodeJoiningStatusRecord[] {
    return session.nodeStatuses.map((nodeStatus): NodeJoiningStatusRecord => {
      const rawStatus = (nodeStatus.provisioningStatus ?? '').toUpperCase();
      const status: NodeJoiningStatusRecord['status'] = (() => {
        if (nodeStatus.provisioningStatus == null) {
          return session.status === 'ACTIVE' ? 'ATTEMPTING' : 'FAILED';
        }

        switch (rawStatus) {
          case 'PROVISIONED':
            return 'JOINED';
          case 'PROVISIONING_ONGOING':
            return 'ATTEMPTING';
          case 'PROVISIONING_INCORRECT':
            return 'FAILED';
          case 'UNPROVISIONED':
            return session.status === 'ACTIVE' ? 'ATTEMPTING' : 'FAILED';
          default:
            return 'UNKNOWN';
        }
      })();

      const lastRxTime = nodeStatus.nodeStatusResponse?.nodeStatistics?.lastMessageRxTime ?? 0;
      const gatewayStatus = nodeStatus.lastMessageGatewayId == null
        ? undefined
        : session.gatewayStatuses.find(
          (gateway) => gateway.gatewayId === nodeStatus.lastMessageGatewayId
        );
      const gatewayLabel = gatewayStatus
        ? gatewayStatus.gatewayName ??
          gatewayStatus.gatewaySerialNumber ??
          gatewayStatus.gatewayJoinStatusResponse?.gatewayId ??
          `Gateway ${nodeStatus.lastMessageGatewayId}`
        : nodeStatus.lastMessageGatewayId == null
          ? 'N/A'
          : `Gateway ${nodeStatus.lastMessageGatewayId}`;
      const gatewaySerialNumber =
        gatewayStatus?.gatewaySerialNumber ?? gatewayStatus?.gatewayJoinStatusResponse?.gatewayId ?? null;

      return {
        nodeAddress: nodeStatus.nodeAddress.toString(16).toUpperCase(),
        uid: nodeStatus.nodeUid ?? '',
        status,
        gateway: gatewayLabel,
        gatewaySerialNumber,
        firmware: '',
        lastActivity: lastRxTime ? formatRelativeTime(lastRxTime) : 'N/A'
      };
    });
  }

  getSessionHistory(buildingId: number): Observable<LatestOpenJoiningSessionStatus[]> {
    return this.nmsResource.getSessionHistory(buildingId);
  }

  loadBLEKeyDelivery(buildingId: number): Observable<BLEKeyDeliveryProgressApiResponse> {
    return this.nmsResource
      .loadBLEKeyDelivery(buildingId)
      .pipe(map((response) => this.normalizeBLEKeyDeliveryResponse(response)));
  }

  retryBLEKeyDelivery(buildingId: number, workflowId: number): Observable<BLEKeyDeliveryRetryApiResponse> {
    return this.nmsResource.retryBLEKeyDelivery(buildingId, workflowId);
  }

  private normalizeBLEKeyDeliveryResponse(
    response: BLEKeyDeliveryProgressApiResponse
  ): BLEKeyDeliveryProgressApiResponse {
    const candidateSteps = response.steps?.length ? response.steps : [];

    const steps = candidateSteps
      .filter((step) => step.step === 'BLE_KEY_DELIVERY')
      .map((step) => {
        const totalNodes = Number(step.totalNodes ?? 0);
        const completedNodes = Number(step.completedNodes ?? 0);
        const failedNodes = Number(step.failedNodes ?? 0);

        return {
          workflowStepId: step.workflowStepId ?? 0,
          step: step.step ?? 'BLE_KEY_DELIVERY',
          name: step.name ?? 'BLE key delivery',
          totalNodes,
          completedNodes,
          failedNodes,
          pendingNodes: Math.max(0, totalNodes - (completedNodes + failedNodes)),
          message: step.message || '',
          status: step.status,
          nodes: step.nodes || []
        };
      });

    return {
      steps,
      completedNodes: response.completedNodes,
      failedNodes: response.failedNodes,
      workflowId: response.workflowId,
      totalNodes: response.totalNodes,
      message: response.message,
      status: response.status,
      openJoiningSessionId: Number(response.openJoiningSessionId ?? 0),
      updatedAt: response.updatedAt
    };
  }

}